// Shared by the preflight check and visible images. Keep successful Image objects
// alive so widgets reuse the exact URL that passed, including a recovered URL.
const loaded = new Map();
const pending = new Map();

export function retryImageUrl(src, attempt) {
  // Signed external URLs must keep their query string intact.
  if (!attempt || /^(data:|blob:)/i.test(src)) return src;
  const url = new URL(src, window.location.href);
  if (url.origin !== window.location.origin) return src;
  url.searchParams.set("_imageRetry", `${Date.now()}-${attempt}`);
  return url.href;
}

export function forgetExamImage(src) {
  loaded.delete(src);
}

export function loadExamImage(src, { attempts = 3, timeout = 12000, delay = 500 } = {}) {
  if (loaded.has(src)) return Promise.resolve(loaded.get(src).src);
  if (pending.has(src)) return pending.get(src);
  const task = (async () => {
    for (let attempt = 0; attempt < attempts; attempt++) {
      if (attempt) await new Promise(resolve => setTimeout(resolve, delay * attempt));
      try {
        const image = await new Promise((resolve, reject) => {
          const img = new Image();
          const finish = (ok) => {
            clearTimeout(timer);
            img.onload = img.onerror = null;
            if (ok && img.naturalWidth > 0) resolve(img);
            else reject(new Error(`Image unavailable: ${src}`));
          };
          const timer = setTimeout(() => finish(false), timeout);
          img.onload = () => finish(true);
          img.onerror = () => finish(false);
          img.src = retryImageUrl(src, attempt);
        });
        loaded.set(src, image);
        return image.src;
      } catch (error) {
        if (attempt === attempts - 1) throw error;
      }
    }
  })();
  pending.set(src, task);
  task.then(() => pending.delete(src), () => pending.delete(src));
  return task;
}

// Walk nested choices, rows, trials, prompts, and World of Music sections.
// Do not treat recordings, MusicXML, or answer-key URLs as image assets.
export function collectExamImages(exam) {
  const assets = new Map();
  function visit(node, label, imageContext = false) {
    if (!node || typeof node !== "object") return;
    if (Array.isArray(node)) { node.forEach(item => visit(item, label, imageContext)); return; }
    const here = node.config?.number ?? node.number;
    if (here != null) label = `Q${here}`;
    const context = imageContext || node.type === "image";
    for (const [key, value] of Object.entries(node)) {
      if (/^(answers?|answerKey|feedback)$/i.test(key)) continue;
      const imageKey = /image$/i.test(key);
      if (typeof value === "string" && value.trim() &&
          (imageKey || (key === "src" && (context || /\.(png|jpe?g|gif|webp|svg|avif|bmp)([?#]|$)/i.test(value))))) {
        if (!assets.has(value)) assets.set(value, new Set());
        assets.get(value).add(label || "Exam");
      } else if (typeof value === "object") {
        visit(value, label, imageKey || /images$/i.test(key));
      }
    }
  }
  visit(exam, "Exam");
  return [...assets].map(([src, labels]) => ({ src, labels: [...labels] }));
}

export async function checkExamImages(assets, concurrency = 6) {
  let next = 0;
  const failed = [];
  await Promise.all(Array.from({ length: Math.min(concurrency, assets.length) }, async () => {
    while (next < assets.length) {
      const asset = assets[next++];
      try { await loadExamImage(asset.src); }
      catch { failed.push(asset); }
    }
  }));
  return failed;
}
